import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Breadcrumbs, Button, Select } from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';

const axios = require('axios').default;

const DEFAULT_CONTACT_LABEL = 'default';

const toSelectOptions = (labelOptions) => Object.entries(labelOptions).map(([value, forms]) => ({ value, label: forms.plural }));

function CrmLabelsPage({
  csrfToken, team: initialTeam, contactLabelOptions, dealLabelOptions, selectedContactLabel, defaultContactLabel, selectedDealLabel,
  organizationLabelOptions, selectedOrganizationLabel,
}) {
  const [team, setTeam] = useState(initialTeam);
  const [contactLabel, setContactLabel] = useState(selectedContactLabel || DEFAULT_CONTACT_LABEL);
  const [dealLabel, setDealLabel] = useState(selectedDealLabel);
  const [organizationLabel, setOrganizationLabel] = useState(selectedOrganizationLabel);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(
        `/teams/${team.id}/crm_labels.json`,
        { team: { crm_contact_label: contactLabel === DEFAULT_CONTACT_LABEL ? null : contactLabel, crm_deal_label: dealLabel, crm_organization_label: organizationLabel } },
        { headers: { 'X-CSRF-TOKEN': csrfToken } },
      );
      setTeam({ ...team, crm_labels: response.data.crmLabels });
      toast.success('Labels saved.');
    } catch (error) {
      const messages = error.response && error.response.data && error.response.data.errors;
      toast.error(Array.isArray(messages) ? messages.join(', ') : 'Failed to save labels.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={<Breadcrumbs items={[{ label: 'Settings', href: `/teams/${team.id}/crm-settings` }, { label: 'Labels' }]} />}
      title="Labels"
      subtitle={team.deals_enabled !== false ? 'Choose what your team calls the people, organizations and deals in your CRM.' : 'Choose what your team calls the people and organizations in your CRM.'}
    >
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <Select
              label="People"
              options={[
                { value: DEFAULT_CONTACT_LABEL, label: `Default (${defaultContactLabel})` },
                ...toSelectOptions(contactLabelOptions),
              ]}
              value={contactLabel}
              onChange={setContactLabel}
            />
          </div>
          <div className="mb-3">
            <Select
              label="Organizations"
              options={toSelectOptions(organizationLabelOptions)}
              value={organizationLabel}
              onChange={setOrganizationLabel}
            />
          </div>
          {team.deals_enabled !== false && (
            <div className="mb-3">
              <Select
                label="Deals"
                options={toSelectOptions(dealLabelOptions)}
                value={dealLabel}
                onChange={setDealLabel}
              />
            </div>
          )}
          <Button onClick={save} disabled={saving}>
            Save
          </Button>
        </div>
      </div>
    </CrmPageLayout>
  );
}

const labelOptionsShape = PropTypes.objectOf(PropTypes.shape({
  singular: PropTypes.string,
  plural: PropTypes.string,
}));

CrmLabelsPage.propTypes = {
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  contactLabelOptions: labelOptionsShape.isRequired,
  dealLabelOptions: labelOptionsShape.isRequired,
  selectedContactLabel: PropTypes.string,
  defaultContactLabel: PropTypes.string.isRequired,
  selectedDealLabel: PropTypes.string.isRequired,
  organizationLabelOptions: labelOptionsShape.isRequired,
  selectedOrganizationLabel: PropTypes.string.isRequired,
};

export default CrmLabelsPage;
